// Client-side reading of `matchEnd.stats` (protocol.ts). Tolerant, not strict: the results screen is a
// nicety, so a field this build doesn't know or a counter that came through malformed must degrade to 0
// rather than cost the player the whole screen. Kept in shared/ so the server's own tests can prove the
// payload it sends survives this exact parser.
import { HEROES, type HeroKind } from '../core/heroes.js';
import type { Team } from '../core/layout.js';
import { RELICS } from '../core/relics.js';
import type { RelicLine } from '../core/commands.js';
import { emptyHeroStats, type HeroStatLine, type HeroStats, type MatchStats, type RelicRecord, type RelicTally } from '../core/stats.js';

const NAME_MAX = 24;  // the server's own display-name cap (see SeatView.name)
const MAX_HEROES = 8;
const MAX_RELIC_RECORDS = 32;  // far above any real match: 4 lines, each bought again only after a death
export const STAT_KEYS = Object.keys(emptyHeroStats()) as (keyof HeroStats)[];

/** The wire's `matchEnd.stats`, tolerantly: a missing or non-numeric counter reads 0, a malformed hero entry
    is dropped, and anything without a single usable hero is null (the caller falls back to the plain banner). */
export function parseMatchStats(raw: unknown): MatchStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { duration?: unknown; heroes?: unknown; relics?: unknown };
  if (!Array.isArray(r.heroes)) return null;
  const heroes: HeroStatLine[] = [];
  for (const e of r.heroes.slice(0, MAX_HEROES)) {
    if (!e || typeof e !== 'object') continue;
    const h = e as Record<string, unknown>;
    if (typeof h.id !== 'number' || !Number.isInteger(h.id) || h.id < 0) continue;
    if (typeof h.kind !== 'string' || !Object.hasOwn(HEROES, h.kind)) continue;
    if (h.team !== 'a' && h.team !== 'b') continue;
    const line = { ...emptyHeroStats(), id: h.id, kind: h.kind as HeroKind, team: h.team as Team,
      name: typeof h.name === 'string' ? h.name.slice(0, NAME_MAX) : null } as HeroStatLine;
    for (const k of STAT_KEYS) { const v = h[k]; if (typeof v === 'number' && Number.isFinite(v) && v >= 0) line[k] = v; }
    heroes.push(line);
  }
  if (!heroes.length) return null;
  const d = r.duration, relics = parseRelicTally(r.relics);
  return { duration: typeof d === 'number' && Number.isFinite(d) && d >= 0 ? d : 0, heroes, ...(relics ? { relics } : {}) };
}

/** Absent or not an object: none (a pre-protocol-14 payload). A team that is not a list (the protocol-14/15
    counters) reads as no records; an entry with an unknown line is dropped, a non-true `lost` reads as held. */
function parseRelicTally(raw: unknown): RelicTally | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const team = (v: unknown): RelicRecord[] => {
    if (!Array.isArray(v)) return [];
    const out: RelicRecord[] = [];
    for (const e of v.slice(0, MAX_RELIC_RECORDS)) {
      if (!e || typeof e !== 'object') continue;
      const { line, lost } = e as Record<string, unknown>;
      if (typeof line !== 'string' || !Object.hasOwn(RELICS, line)) continue;
      out.push({ line: line as RelicLine, lost: lost === true });
    }
    return out;
  };
  const o = raw as Record<string, unknown>;
  return { a: team(o.a), b: team(o.b) };
}
